// #417 / #687 — State Selector UI.
//
// A Vue component that renders in multiple slots on the same page:
//   - Desktop header utility area (announcement bar per Audrina's design)
//   - Mobile menu (per Audrina, mobile bar is too tight for the dropdown)
//   - Quick Shop sidebar (per Kyle 2026-03-26, "we need state in quick shop")
//
// All instances read from / write to the shared GlobalState.state, which is
// Vue-observable. Changing the state in one instance is reflected everywhere.
//
// DOM contract: include `{% render 'state-selector' %}` in any slot. The
// snippet renders a `<div data-state-selector>...</div>` plus a
// `<script type="text/x-template" id="state-selector-template">` once at the
// layout level. On init, we mount one Vue instance per `data-state-selector`
// element found.

import Vue from 'vue';
import { GlobalState, store } from '../../global-state/global-state';
import { AU_STATE_CODES } from '../../utils/geo-state';
import { STATE_EVENTS } from '../../utils/state-events';

// Track every mounted state-selector instance so the global
// `tena:select-state` handler below can delegate to one without walking the
// Vue tree. Multiple instances exist on the same page (announcement bar,
// mobile menu, quickshop) — picking the first live one is enough since they
// all share GlobalState.
const mountedInstances = [];

// Register the global listener exactly once. Other components (the PDP zone
// list, the quickshop warehouse panel) dispatch `tena:select-state` to ask
// the state selector to apply a new value. We delegate to the first mounted
// instance so the existing Vue `$storage` / event-dispatch contract stays
// intact and we don't duplicate the persistence logic.
let globalListenerRegistered = false;
function ensureGlobalSelectStateListener() {
  if (globalListenerRegistered) return;
  globalListenerRegistered = true;
  window.addEventListener(STATE_EVENTS.SELECT, (e) => {
    const code = e && e.detail && e.detail.state;
    if (!code || !AU_STATE_CODES.includes(code)) return;
    const target = mountedInstances[0];
    if (target && typeof target.selectState === 'function') target.selectState(code);
  });
}

export const StateSelectorComponent = {
  template: '#state-selector-template',
  data() {
    return {
      sharedState: store,
      isOpen: false,
      states: AU_STATE_CODES.map((code) => ({ code })),
    };
  },
  computed: {
    currentState() {
      return this.sharedState.selectedState || this.sharedState.detectedState || '';
    },
    hasSelection() {
      return !!this.sharedState.selectedState;
    },
    triggerLabel() {
      if (this.currentState) return this.currentState;
      return 'Select';
    },
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    closeDropdown() {
      this.isOpen = false;
    },
    selectState(code) {
      if (!AU_STATE_CODES.includes(code)) return;
      GlobalState.setSelectedState(this, code);
      this.isOpen = false;
      window.dispatchEvent(new CustomEvent(STATE_EVENTS.SELECTED, {
        detail: { state: code },
      }));
    },
    clearSelection() {
      GlobalState.setSelectedState(this, '');
      this.isOpen = false;
      window.dispatchEvent(new CustomEvent(STATE_EVENTS.CLEARED));
    },
    // Close on outside click.
    handleDocumentClick(e) {
      if (!this.$el.contains(e.target)) this.isOpen = false;
    },
  },
  mounted() {
    mountedInstances.push(this);
    ensureGlobalSelectStateListener();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    const idx = mountedInstances.indexOf(this);
    if (idx !== -1) mountedInstances.splice(idx, 1);
    document.removeEventListener('click', this.handleDocumentClick);
  },
};

// Register globally once so any Vue instance on the page (including the
// quickshop sidebar) can embed `<state-selector></state-selector>` without
// re-registering.
let registered = false;
function registerGlobal() {
  if (registered) return;
  Vue.component('state-selector', StateSelectorComponent);
  registered = true;
}

// Mount a Vue instance on every `[data-state-selector]` element NOT already
// inside another Vue root. Safe to call multiple times (idempotent: skips
// already-mounted nodes).
//
// For slots inside an existing Vue root (e.g. quickshop sidebar), don't add
// the data-attribute — just drop `<state-selector></state-selector>` into the
// parent x-template and the global registration above handles it.
export default function StateSelector() {
  registerGlobal();
  const nodes = document.querySelectorAll('[data-state-selector]:not([data-state-selector-mounted])');
  nodes.forEach((node) => {
    node.setAttribute('data-state-selector-mounted', 'true');
    // eslint-disable-next-line no-new
    new Vue({
      el: node,
      template: '<state-selector></state-selector>',
    });
  });
}
